import type { PendingTransferRow } from '../services/vehicleTransferService';
export type UnitFlagKey =
  | 'pmOverdue'
  | 'greasingOverdue'
  | 'cvipOverdue'
  | 'cvipDueSoon'
  | 'criticalDefects'
  | 'openDefects'
  | 'insuranceExpired'
  | 'insuranceMissing'
  | 'notVisibleToDrivers'
  | 'pastExpectedReturn';

export interface UnitFlag {
  key: UnitFlagKey;
  label: string;
  /** Tooltip sentence, e.g. "PM overdue by 118 km". */
  detail: string;
  tone: 'danger' | 'warning' | 'neutral';
}

export interface Vehicle {
  id: number;
  unitNumber: string;
  timberlineUnitNumber?: string;
  description?: string | null;
  /** FleetStatus value: Active | Inactive | StoredServiceable | Mothballed | PartsUnit | Retired. */
  status: string;
  numberOfAxles?: number;
  /** Licence plate (EQ-20). */
  plateNumber?: string | null;
  year?: number;
  rate?: number;
  /** RateMode value: RateClass | Custom. See EQ-M03 follow-up (Equipment tab). */
  rateMode?: RateMode | string | null;
  /** e.g. "RC-01" - only meaningful when rateMode is RateClass. */
  rateClassCode?: string | null;
  /** On-road / off-road (WRAP Vehicle Type). */
  vehicleType?: string;
  /** Kind of unit (grader, plow truck, etc.). TrueContext "Requires Trip Inspection". */
  vehicleClassification?: string | null;
  trailerOrAttachment?: string;
  fuelType?: string;
  brakeTypeFront?: string;
  brakeTypeRear?: string;
  hasGPS?: boolean;
  canPatrol?: boolean;
  comments?: string;
  imageUrl?: string;
  currentStatus?: string;
  availability?: VehicleAvailability | string | null;
  availabilityReason?: string | null;
  gpsModemId?: string | null;
  roadClass?: 'ON_ROAD' | 'OFF_ROAD' | string | null;
  currentOdometer?: number;
  lastOdometerReportedType?: string;
  lastOdometerReportedDateTime?: string;
  nextPMDue?: number;
  nextCVIP?: string;
  nextPMType?: string;
  nextMinor?: number;
  nextMajor?: number;
  nextGreasing?: number;
  estimatedReturnToServiceDate?: string;
  parkedReady?: boolean;
  lastParked?: string;
  vehicleProfileId?: number | null;
  vehicleProfile?: VehicleProfile;
  vehicleServiceAreas?: VehicleServiceArea[];
  vehicleHistory?: VehicleHistory[];
  vehicleComponents?: VehicleComponent[];
  blueBookRates?: VehicleBlueBookRate[];
  vehicleAttachments?: VehicleAttachment[];
  // New fields
  vinNumber?: string;
  make?: string;
  model?: string;
  summerConfiguration?: SummerConfiguration;
  winterConfiguration?: WinterConfiguration;
  spreaderSystemType?: string;
  warrantyStatus?: WarrantyStatus;
  registrationNumber?: string;
  tareWeight?: number;
  wheelbase?: number;
  gvwr?: number;
  height?: number;
  length?: number;
  width?: number;
  calculatedCenterOfGravity?: number;
  currentLocation?: string;
  ownershipStatus?: OwnershipStatus;
  // EQ-M03: home yard / current yard (ContractArea references)
  homeYardId?: number | null;
  currentYardId?: number | null;
  homeYard?: VehicleYard;
  currentYard?: VehicleYard;
  foremanAreaId?: number | null;
  foremanArea?: { id: number; name: string; serviceAreaId?: number };
  driverVisibility?: DriverVisibility;
  deactivationWarnings?: string[];
  insured?: boolean;
  insuranceExpiry?: string | null;
  openDefectCount?: number;
  /** EQ-15: worked out on the server, the same on the list, the board and the header. */
  flags?: UnitFlag[];
  availabilityEffective?: VehicleAvailability | string | null;
  availabilityLabel?: string;
  insurances?: VehicleInsurance[];
  defects?: VehicleDefect[];
  odometerReadings?: VehicleOdometerReading[];
  createdAt?: string;
  updatedAt?: string;
}

export type DriverVisibilityFail = 'fleet_status' | 'availability' | 'insurance';

export interface DriverVisibility {
  visible: boolean;
  failed: DriverVisibilityFail[];
}

export enum VehicleAvailability {
  InService = 'InService',
  OutOfService = 'OutOfService'
}

export interface VehicleInsurance {
  id: number;
  vehicleId: number;
  policyNumber: string;
  insurer: string;
  effectiveFrom: string;
  expires: string;
  documentUrl?: string | null;
}

export type VehicleDefectStatus = 'Open' | 'Closed';
export type VehicleDefectOutcome = 'Repaired' | 'Duplicate' | 'NoFaultFound';
export type PersonRef = { id: number; name: string; lastName: string };

export interface VehicleDefect {
  id: number;
  vehicleId: number;
  source: string;
  severity: string;
  status: VehicleDefectStatus | string;
  /** Inspection category shared by PMs, trip inspections and WRAP (EQ-13). */
  category?: string | null;
  description: string;
  reporterName: string;
  reportedAt: string;
  remedy?: string | null;
  closedAt?: string | null;
  photoUrl?: string | null;
  reportedBy?: { id: number; name: string; lastName: string } | null;
  closedBy?: { id: number; name: string; lastName: string } | null;
  remediedAt?: string | null;
  remediedBy?: { id: number; name: string; lastName: string } | null;
  /** Resolve Defect (EQ-07). */
  outcome?: VehicleDefectOutcome | null;
  fixedAt?: string | null;
  fixedBy?: PersonRef | null;
  signedOffAt?: string | null;
  signedOffBy?: PersonRef | null;
  duplicateOfId?: number | null;
  duplicateOf?: { id: number; description: string; category?: string | null } | null;
  warning?: string;
  duplicate?: boolean;
}

export interface VehicleDefectExportRow extends VehicleDefect {
  /** Latest odometer reading on or before the defect was reported. */
  odometer: number | null;
}

export interface VehicleDefectExport {
  unitNumber: string;
  from: string | null;
  to: string | null;
  defects: VehicleDefectExportRow[];
}

export interface VehiclePmInspectionItem {
  id: number;
  inspectionId: number;
  sortOrder: number;
  inspectionType: string;
  inspectionValue: string;
  inspectionItem: string;
  unitOfMeasurement?: string | null;
  measurementValue?: string | null;
  comments?: string | null;
}

export interface VehiclePmInspection {
  id: number;
  vehicleId: number;
  performedAt: string;
  employeeName: string;
  pmType: string;
  odometer?: number | null;
  referenceNumber?: string | null;
  /** Present on the list endpoint. */
  itemCount?: number;
  failCount?: number;
  /** Present on the detail endpoint and the print-all list. */
  items?: VehiclePmInspectionItem[];
}

export interface VehicleOdometerReading {
  id: number;
  vehicleId: number;
  value: number;
  source: string;
  reporterName: string;
  recordedAt: string;
  override: boolean;
  /** Why a backwards reading was overridden (EQ-19). */
  reason?: string | null;
}

export interface EquipmentRepairRow {
  id: number;
  effortId?: number | null;
  /** The daily log submission: /submissions?formId= */
  formId?: number | null;
  date: string | null;
  /** Odometer the unit was at on that day (latest reading on or before it). */
  odometer: number | null;
  employeeName: string | null;
  status: string | null;
  comments: string | null;
  /** Only sent to users with View Repairs. */
  hours?: number;
  rate?: number;
  unitOfMeasure?: string;
  taskName: string | null;
  taskNumber: string | null;
}

/** One repair in full (EQ-26). */
export interface EquipmentRepairDetail extends EquipmentRepairRow {
  taskId: number | null;
  otherEmployees: string[];
  materials: Array<{ name: string; quantity: number | null; unit: string | null }>;
}

export interface EquipmentDashboardSummary {
  groups: Array<{
    id: string;
    label: string;
    tiles: Array<{ key: string; label: string; count: number; href: string }>;
  }>;
}

/** Minimal ContractArea shape used as a "yard" reference on a unit. See EQ-M03. */
export interface VehicleYard {
  id: number;
  name: string;
  code?: string | null;
}

export interface VehicleServiceArea {
  id: number;
  vehicleId: number;
  serviceAreaId: number;
  serviceArea: {
    id: number;
    name: string;
    code?: string;
  };
}

export interface VehicleProfile {
  id: number;
  inspection: string;
  vehicleType?: string;
  vehicleClassification?: string;
  pmClassification?: string;
  odometerAlerts?: string;
  mandatoryOdometerPicture?: boolean;
  requiresCVIExpiryDate?: boolean;
  requiresOdometerReading?: boolean;
  requiresBrakeInspection?: boolean;
  requiresPostTrip?: boolean;
  requiresForemanApproval?: boolean;
  requiresMechSupervisorApproval?: boolean;
  postTripThreshold?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleHistory {
  id: number;
  vehicleId: number;
  field?: string;
  oldValue?: string;
  newValue?: string;
  comments?: string;
  stage: string;
  external: boolean;
  changedBy?: number;
  createdAt: string;
  changedByUser?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
  };
}

export interface VehicleFilters {
  currentStatus?: string;
  serviceArea?: string;
  /** Server resolves home foreman area to service area for allocation filter (ignored when `search` is set). */
  contextForemanAreaId?: string;
  vehicleClassification?: string;
  /** ContractArea id (yard) the unit is currently located at. See EQ-M03. */
  currentYard?: string;
  /** On-road / off-road (WRAP Vehicle Type). */
  vehicleType?: string;
  /** FleetStatus value: Active | Inactive | StoredServiceable | Mothballed | PartsUnit | Retired. */
  fleetStatus?: string;
  /** RateMode value: RateClass | Custom. */
  rateMode?: string;
  /** ForemanArea id the unit is assigned to. */
  foremanArea?: string;
  search?: string;
  availability?: string;
  overduePm?: string;
  overdueGreasing?: string;
  cvipDue?: string;
  insuranceExpiring?: string;
  recentDefects?: string;
  notVisibleToDrivers?: string;
  /** Restrict list to EQ-S03 driver-visible units (Active + in service + insured). */
  visibleToDrivers?: string;
  /** '1': only units with an open service-area transfer (the pinned rows). */
  pendingTransfer?: string;
  /** '1': return open transfers separately as `pendingTransfers`, pinned above the page. */
  pinTransfers?: string;
  /** Server-side sort (allowlisted): unitNumber, description, vehicleType, vehicleClassification, currentStatus. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FleetStatusImpact {
  openAssignmentCount: number;
  activeDailyLogCount: number;
  warnings: string[];
}

export interface VehicleListResponse {
  data: Vehicle[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  /** With `pinTransfers=1`: units with an open service-area transfer, shown above the page. */
  pendingTransfers?: PendingTransferRow<Vehicle>[];
}

export interface VehicleFilterOptions {
  currentStatuses: string[];
  serviceAreas: { id: number; name: string; code?: string }[];
  vehicleClassifications: string[];
  /** Distinct yards currently in use as a unit's current yard. See EQ-M03. */
  currentYards: { id: number; name: string; code?: string }[];
  /** Distinct vehicleType values in use (On-Road / Off-Road). */
  vehicleTypes: string[];
  foremanAreas: { id: number; name: string }[];
}

/** Unit lifecycle state shown as "Fleet status" on the Equipment list. */
export enum FleetStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  StoredServiceable = 'StoredServiceable',
  Mothballed = 'Mothballed',
  PartsUnit = 'PartsUnit',
  Retired = 'Retired'
}

export const FLEET_STATUS_LABELS: Record<FleetStatus, string> = {
  [FleetStatus.Active]: 'Active',
  [FleetStatus.Inactive]: 'Inactive',
  [FleetStatus.StoredServiceable]: 'Stored – Serviceable',
  [FleetStatus.Mothballed]: 'Mothballed',
  [FleetStatus.PartsUnit]: 'Parts Unit',
  [FleetStatus.Retired]: 'Retired'
};

/** Whether a unit is priced from a shared rate class or has a one-off custom rate. */
export enum RateMode {
  RateClass = 'RateClass',
  Custom = 'Custom'
}

export interface VehicleProfileFormData {
  inspection: string;
  vehicleType?: string;
  vehicleClassification?: string;
  pmClassification?: string;
  odometerAlerts?: string;
  mandatoryOdometerPicture?: boolean;
  requiresCVIExpiryDate?: boolean;
  requiresOdometerReading?: boolean;
  requiresBrakeInspection?: boolean;
  requiresPostTrip?: boolean;
  requiresForemanApproval?: boolean;
  requiresMechSupervisorApproval?: boolean;
  postTripThreshold?: number;
}

export enum SummerConfiguration {
  PlowWithSolidSpreader = 'PlowWithSolidSpreader',
  PlowWithBrine = 'PlowWithBrine',
  WaterTruck = 'WaterTruck',
  SignTruckWithMobileCrane = 'SignTruckWithMobileCrane',
  BridgeTruckWithMobileCrane = 'BridgeTruckWithMobileCrane'
}

export enum WinterConfiguration {
  Tractor = 'Tractor'
}

export enum WarrantyStatus {
  Active = 'Active',
  Expired = 'Expired'
}

export enum OwnershipStatus {
  Owned = 'Owned',
  Leased = 'Leased',
  Rented = 'Rented'
}

export enum ComponentType {
  Engine = 'Engine',
  Transmission = 'Transmission',
  Differentials = 'Differentials',
  HydraulicPump = 'HydraulicPump'
}

export interface VehicleComponent {
  id: number;
  vehicleId: number;
  serialNumber?: string;
  componentType?: ComponentType;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleBlueBookRate {
  id: number;
  vehicleId: number;
  rate: number;
  year: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleAttachment {
  id: number;
  vehicleId: number;
  name?: string;
  description?: string;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  /** Set for files that belong to a service-area transfer; they download through that transfer. */
  vehicleTransferId?: number | null;
  transferPhotoSide?: 'FRONT' | 'BACK' | 'LEFT' | 'RIGHT' | null;
  createdAt?: string;
  updatedAt?: string;
}

/* ---- Fleet list, Shop board and Dashboard filters (EQ-14, EQ-16) ---- */

/** Fleet list filters: the list filters plus the Dashboard tile filters. Id lists are comma-separated. */
export interface FleetFilters extends VehicleFilters {
  /** EQ-01: 'overdue' or 'dueSoon' (Next CVIP within 30 days). */
  cvip?: string;
  /** EQ-16: no insurance policy on file. */
  noInsurance?: string;
  /** EQ-16: an open defect reported more than 30 days ago. */
  openDefectsOver30?: string;
  /** EQ-11 / EQ-16: out of service with an expected return before today. */
  pastExpectedReturn?: string;
}

/** The Service Area / Foreman Area scope of the Shop board and Dashboard (comma-separated ids). */
export interface FleetScope {
  serviceArea?: string;
  foremanArea?: string;
}

export interface FleetForemanAreaOption {
  id: number;
  name: string;
  serviceAreaId: number | null;
  serviceArea: { id: number; name: string; code?: string | null } | null;
}

/** /vehicles/filter-options with foreman areas grouped by service area and the caller's own areas. */
export interface FleetFilterOptions extends Omit<VehicleFilterOptions, 'foremanAreas'> {
  foremanAreas: FleetForemanAreaOption[];
  /** The signed-in user's own foreman areas: the Shop board and Dashboard open on these. */
  myForemanAreaIds: number[];
}

export interface FleetDashboardTile {
  key: string;
  label: string;
  count: number;
  /** The Fleet list with the tile's filters and the Dashboard's scope. */
  href: string;
  filters?: Record<string, string>;
}

export interface FleetDashboardSummary {
  groups: Array<{ id: string; label: string; tiles: FleetDashboardTile[] }>;
}
